import React, { useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DecisionToolbar = ({
  onMerge = () => {},
  onKeepSeparate = () => {},
  onSkipForLater = () => {},
  onUndo = () => {},
  canUndo = false,
  isProcessing = false,
  className = ''
}) => {
  const handleKeyPress = (event) => {
    if (isProcessing) return;
    
    switch (event?.key) {
      case '1':
        onMerge();
        break;
      case '2':
        onKeepSeparate();
        break;
      case '3':
        onSkipForLater();
        break;
      case 'z':
        if (event?.ctrlKey || event?.metaKey) {
          event?.preventDefault();
          if (canUndo) onUndo();
        }
        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isProcessing, canUndo]);

  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
      <div className="flex flex-col space-y-4">
        {/* Main Decision Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="success"
            onClick={onMerge}
            disabled={isProcessing}
            iconName="Merge"
            iconPosition="left"
            className="flex-1"
          >
            Merge Products
            <span className="hidden sm:inline ml-2 text-xs opacity-75">(1)</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={onKeepSeparate}
            disabled={isProcessing}
            iconName="Split"
            iconPosition="left"
            className="flex-1"
          >
            Keep Separate
            <span className="hidden sm:inline ml-2 text-xs opacity-75">(2)</span>
          </Button>
          
          <Button
            variant="secondary"
            onClick={onSkipForLater}
            disabled={isProcessing}
            iconName="Clock"
            iconPosition="left"
            className="flex-1"
          >
            Skip for Later
            <span className="hidden sm:inline ml-2 text-xs opacity-75">(3)</span>
          </Button>
        </div>

        {/* Secondary Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onUndo}
              disabled={!canUndo || isProcessing}
              iconName="Undo2"
              iconPosition="left"
            >
              Undo
              <span className="hidden sm:inline ml-1 text-xs opacity-75">(Ctrl+Z)</span>
            </Button>
          </div>

          {/* Keyboard Shortcuts Info */}
          <div className="hidden md:flex items-center space-x-4 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">1</kbd>
              <span>Merge</span>
            </div>
            <div className="flex items-center space-x-1">
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">2</kbd>
              <span>Separate</span>
            </div>
            <div className="flex items-center space-x-1">
              <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">3</kbd>
              <span>Skip</span>
            </div>
          </div>
        </div>

        {/* Decision Explanations */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-border">
          <div className="text-center p-2 bg-success bg-opacity-5 rounded">
            <Icon name="Merge" size={16} className="text-success mx-auto mb-1" />
            <p className="text-xs text-success font-medium">Merge Products</p>
            <p className="text-xs text-muted-foreground">
              Combine as same product
            </p>
          </div>
          
          <div className="text-center p-2 bg-muted bg-opacity-50 rounded">
            <Icon name="Split" size={16} className="text-muted-foreground mx-auto mb-1" />
            <p className="text-xs text-foreground font-medium">Keep Separate</p>
            <p className="text-xs text-muted-foreground">
              Treat as different products
            </p>
          </div>
          
          <div className="text-center p-2 bg-secondary bg-opacity-10 rounded">
            <Icon name="Clock" size={16} className="text-secondary mx-auto mb-1" />
            <p className="text-xs text-secondary font-medium">Skip for Later</p>
            <p className="text-xs text-muted-foreground">
              Review in next session
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionToolbar;